import { defineStore } from "pinia"
import { ref, inject, computed } from "vue"
import { useFetchWrapper } from "@core/composables"
import type {
    ConfigVariable,
    SourceOperator,
    SourceBrand,
    SourceModel,
    OperatorModelsResponse,
    Scope,
    Supplier,
} from "@types"
import type { Services } from "@services"
import { DEFAULT_SCOPE_NAME } from "@core/constants"

/**
 * Store permettant de gérer le cache des données de configuration
 * (variables, opérateurs, marques, modèles) avec lazy loading
 */
export const useConfigStore = defineStore("config", () => {
    // ================================
    // ÉTAT
    // ================================

    /**
     * Liste des variables de configuration
     */
    const variables = ref<ConfigVariable[]>([])

    /**
     * Liste des opérateurs
     */
    const operators = ref<SourceOperator[]>([])

    /**
     * Liste des marques de sources
     */
    const sourceBrands = ref<SourceBrand[]>([])

    /**
     * Liste des modèles de sources
     */
    const sourceModels = ref<SourceModel[]>([])

    /**
     * Cache des modèles et marques par opérateur
     */
    const operatorModelsCache = ref<Record<string, OperatorModelsResponse>>({})

    /**
     * Scope courant (marque / tenant)
     */
    const scope = ref<Scope | null>(null)

    /**
     * Liste des fournisseurs d'énergie
     */
    const suppliers = ref<Supplier[]>([])

    // ================================
    // SERVICES
    // ================================

    /**
     * Injection des services
     */
    const services = inject<Services>("services")

    // ================================
    // ACTIONS ASYNCHRONES
    // ================================

    /**
     * Récupère la liste des variables disponibles
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchVariables,
        isFetching: isLoadingVariables,
        hasError: hasErrorVariables,
        error: errorVariables,
    } = useFetchWrapper(async (controller?: AbortController) => {
        // Lazy loading : ne charge que si les données ne sont pas déjà présentes
        if (variables.value.length > 0) {
            return
        }

        if (!services?.$getVariables) {
            throw new Error("Service $getVariables non disponible")
        }

        try {
            variables.value = await services.$getVariables(controller)
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des variables:",
                error
            )
            throw error
        }
    })

    /**
     * Récupère la liste des opérateurs disponibles
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchOperators,
        isFetching: isLoadingOperators,
        hasError: hasErrorOperators,
        error: errorOperators,
    } = useFetchWrapper(async (controller?: AbortController) => {
        // Lazy loading : ne charge que si les données ne sont pas déjà présentes
        if (operators.value.length > 0) {
            return
        }

        if (!services?.$getOperators) {
            throw new Error("Service $getOperators non disponible")
        }

        try {
            operators.value = await services.$getOperators(controller)
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des opérateurs:",
                error
            )
            throw error
        }
    })

    /**
     * Récupère la liste des marques de sources disponibles
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchSourceBrands,
        isFetching: isLoadingSourceBrands,
        hasError: hasErrorSourceBrands,
        error: errorSourceBrands,
    } = useFetchWrapper(async (controller?: AbortController) => {
        // Lazy loading : ne charge que si les données ne sont pas déjà présentes
        if (sourceBrands.value.length > 0) {
            return
        }

        if (!services?.$getSourceBrands) {
            throw new Error("Service $getSourceBrands non disponible")
        }

        try {
            sourceBrands.value = await services.$getSourceBrands(controller)
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des marques de sources:",
                error
            )
            throw error
        }
    })

    /**
     * Récupère la liste des modèles de sources disponibles
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchSourceModels,
        isFetching: isLoadingSourceModels,
        hasError: hasErrorSourceModels,
        error: errorSourceModels,
    } = useFetchWrapper(async (controller?: AbortController) => {
        // Lazy loading : ne charge que si les données ne sont pas déjà présentes
        if (sourceModels.value.length > 0) {
            return
        }

        if (!services?.$getSourceModels) {
            throw new Error("Service $getSourceModels non disponible")
        }

        try {
            sourceModels.value = await services.$getSourceModels(controller)
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des modèles de sources:",
                error
            )
            throw error
        }
    })

    /**
     * Récupère le scope courant
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchScope,
        isFetching: isLoadingScope,
        hasError: hasErrorScope,
        error: errorScope,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (scope.value !== null) {
            return
        }

        if (!services?.$getScope) {
            throw new Error("Service $getScope non disponible")
        }

        try {
            scope.value = await services.$getScope(controller)
        } catch (error) {
            console.error("Erreur lors de la récupération du scope:", error)
            throw error
        }
    })

    /**
     * Récupère les modèles et marques disponibles pour un opérateur donné
     * Ne charge que si les données ne sont pas déjà en cache (lazy loading)
     * @param operator - Identifiant de l'opérateur
     */
    const {
        fetch: fetchOperatorModels,
        isFetching: isLoadingOperatorModels,
        hasError: hasErrorOperatorModels,
        error: errorOperatorModels,
    } = useFetchWrapper(
        async (operator: string, controller?: AbortController) => {
            // Lazy loading : ne charge que si les données ne sont pas déjà en cache
            if (operatorModelsCache.value[operator]) {
                return
            }

            if (!services?.$getOperatorModelsByOperator) {
                throw new Error(
                    "Service $getOperatorModelsByOperator non disponible"
                )
            }

            if (!operator) {
                throw new Error(
                    "Aucun opérateur spécifié pour récupérer les modèles"
                )
            }

            try {
                operatorModelsCache.value[operator] =
                    await services.$getOperatorModelsByOperator(
                        operator,
                        controller
                    )
            } catch (error) {
                console.error(
                    `Erreur lors de la récupération des modèles pour l'opérateur ${operator}:`,
                    error
                )
                throw error
            }
        }
    )

    /**
     * Récupère la liste des fournisseurs d'énergie disponibles
     * Ne charge que si les données ne sont pas déjà présentes (lazy loading)
     */
    const {
        fetch: fetchSuppliers,
        isFetching: isLoadingSuppliers,
        hasError: hasErrorSuppliers,
        error: errorSuppliers,
    } = useFetchWrapper(async (controller?: AbortController) => {
        if (suppliers.value.length > 0) {
            return
        }

        if (!services?.$getSuppliers) {
            throw new Error("Service $getSuppliers non disponible")
        }

        try {
            suppliers.value = await services.$getSuppliers(controller)
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des fournisseurs:",
                error
            )
            throw error
        }
    })

    // ================================
    // RESET
    // ================================

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset(resetScope = false) {
        variables.value = []
        operators.value = []
        sourceBrands.value = []
        sourceModels.value = []
        operatorModelsCache.value = {}
        suppliers.value = []

        if (resetScope) {
            scope.value = null
        }
    }

    // ================================
    // GETTERS
    // ================================

    /**
     * Récupère la liste des variables
     */
    const getVariables = computed(() => variables.value)

    /**
     * Couleur primaire du scope (scope?.assets?.colors?.primary)
     */
    const getScopePrimaryColor = computed(
        () => scope.value?.assets?.colors?.primary ?? "#304282"
    )

    /**
     * Nom et brand du scope (scope?.brand, scope?.name)
     */
    const getScopeName = computed(() => scope.value?.name)
    const getScopeBrand = computed(() => scope.value?.brand)

    /**
     * Logos du scope (full et full_white)
     */
    const getScopeLogoFull = computed(() => scope.value?.assets?.logos?.full)
    const getScopeLogoFullWhite = computed(
        () => scope.value?.assets?.logos?.full_white
    )

    /**
     * Indique si le scope est le scope par défaut
     */
    const isDefaultScope = computed(
        () => getScopeName.value === DEFAULT_SCOPE_NAME
    )
    /**
     * Récupère la liste des opérateurs
     */
    const getOperators = computed(() => operators.value)

    /**
     * Récupère la liste des marques de sources
     */
    const getSourceBrands = computed(() => sourceBrands.value)

    /**
     * Récupère la liste des modèles de sources
     */
    const getSourceModels = computed(() => sourceModels.value)

    /**
     * Récupère la liste des fournisseurs d'énergie
     */
    const getSuppliers = computed(() => suppliers.value)

    // ================================
    // HELPERS
    // ================================

    /**
     * Récupère une variable par son type (identifiant)
     * @param type - Type de la variable (identifiant unique)
     * @returns La variable correspondante ou undefined si non trouvée
     */
    function getVariableByType(type: string): ConfigVariable | undefined {
        return variables.value.find((variable) => variable.type === type)
    }

    /**
     * Récupère un opérateur par son identifiant
     * @param idname - Identifiant de l'opérateur
     * @returns L'opérateur correspondant ou undefined si non trouvé
     */
    function getOperatorById(idname: string): SourceOperator | undefined {
        return operators.value.find((operator) => operator.idname === idname)
    }

    /**
     * Récupère une marque de source par son identifiant
     * @param idname - Identifiant de la marque
     * @returns La marque correspondante ou undefined si non trouvée
     */
    function getSourceBrandById(idname: string): SourceBrand | undefined {
        return sourceBrands.value.find((brand) => brand.idname === idname)
    }

    /**
     * Récupère un modèle de source par son identifiant
     * @param id - Identifiant du modèle
     * @returns Le modèle correspondant ou undefined si non trouvé
     */
    function getSourceModelById(id: string): SourceModel | undefined {
        return sourceModels.value.find((model) => model.idname === id)
    }

    /**
     * Récupère un fournisseur par son identifiant
     * @param _id_supplier - Identifiant du fournisseur
     * @returns Le fournisseur correspondant ou undefined si non trouvé
     */
    function getSupplierById(
        _id_supplier: string | null
    ): Supplier | undefined {
        if (!_id_supplier) return undefined
        return suppliers.value.find(
            (supplier) => supplier._id_supplier === _id_supplier
        )
    }

    // ================================
    // RETURN
    // ================================

    return {
        // État
        variables,
        operators,
        sourceBrands,
        sourceModels,
        operatorModelsCache,
        suppliers,
        scope,

        // Reset
        $reset,

        // Getters
        getVariables,
        getOperators,
        getSourceBrands,
        getSourceModels,
        getSuppliers,
        getScopePrimaryColor,
        getScopeName,
        getScopeBrand,
        getScopeLogoFull,
        getScopeLogoFullWhite,
        isDefaultScope,

        // Actions
        fetchVariables,
        fetchOperators,
        fetchSourceBrands,
        fetchSourceModels,
        fetchScope,
        fetchOperatorModels,
        fetchSuppliers,

        // Helpers
        getVariableByType,
        getOperatorById,
        getSourceBrandById,
        getSourceModelById,
        getSupplierById,

        // États de chargement
        isLoadingVariables,
        isLoadingOperators,
        isLoadingSourceBrands,
        isLoadingSourceModels,
        isLoadingScope,
        isLoadingOperatorModels,
        isLoadingSuppliers,

        // États d'erreur
        hasErrorVariables,
        hasErrorOperators,
        hasErrorSourceBrands,
        hasErrorSourceModels,
        hasErrorScope,
        hasErrorOperatorModels,
        hasErrorSuppliers,

        // Erreurs
        errorVariables,
        errorOperators,
        errorSourceBrands,
        errorSourceModels,
        errorScope,
        errorOperatorModels,
        errorSuppliers,
    }
})
